import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCompanyStats } from '../../hooks/useCompany';
import { useUsers } from '../../hooks/useUsers';
import { useProjects } from '../../hooks/useProjects';
import { Globe, ExternalLink } from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import UserList from '../../components/users/UserList';
import UserForm from '../../components/users/UserForm';
import CompanyActions from '../../components/admin/CompanyActions';
import { useAdmin } from '../../contexts/AdminContext';
import { formatLimit, formatLimitPlural, isUnlimited, isAtLimit, hasLimitWarning, getAvailableSlots } from '../../utils/planLimits';
import type { User } from '../../types/user';

type TabType = 'overview' | 'users' | 'projects' | 'settings';

const CompanyDashboard: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const { setSelectedCompanyId, selectedCompanyId } = useAdmin();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState<'plan' | 'status' | 'portal' | null>(null);

  const { data: statsData, isLoading: statsLoading, error: statsError } = useCompanyStats(companyId || '');
  const { data: users, isLoading: usersLoading } = useUsers({ activeOnly: false });
  const { data: projects, isLoading: projectsLoading } = useProjects('all');

  // Auto-impersonate the company when entering its dashboard
  useEffect(() => {
    if (companyId && companyId !== selectedCompanyId) {
      setSelectedCompanyId(companyId);
    }
  }, [companyId]);

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (statsError || !statsData) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-red-800 mb-2">Error</h2>
        <p className="text-red-700">No se pudo cargar la información de la empresa.</p>
      </div>
    );
  }

  const { company, stats } = statsData;

  const tabs = [
    { id: 'overview' as TabType, name: 'Resumen', icon: '📊' },
    { id: 'users' as TabType, name: 'Usuarios', icon: '👥', count: stats.users.total },
    { id: 'projects' as TabType, name: 'Proyectos', icon: '📁', count: stats.projects.total },
    { id: 'settings' as TabType, name: 'Configuración', icon: '⚙️' },
  ];

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setShowUserForm(true);
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setShowUserForm(true);
  };

  const handleOpenAction = (type: 'plan' | 'status' | 'portal') => {
    setActionType(type);
    setShowActionModal(true);
  };

  const handleCloseAction = () => {
    setActionType(null);
    setShowActionModal(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/companies')}
            className="text-gray-600 hover:text-gray-900"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{company.name}</h1>
            <p className="mt-1 text-sm text-gray-600">
              {company.tradeName || company.rut}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
            company.settings.subscriptionStatus === 'active' 
              ? 'bg-green-100 text-green-800' 
              : 'bg-yellow-100 text-yellow-800'
          }`}>
            {company.settings.subscriptionStatus === 'active' ? 'Activa' : 'Suspendida'}
          </span>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            Plan {company.settings.subscriptionPlan}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2
                ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <span>{tab.icon}</span>
              <span>{tab.name}</span>
              {tab.count !== undefined && (
                <span className={`
                  ml-2 py-0.5 px-2 rounded-full text-xs font-medium
                  ${activeTab === tab.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}
                `}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Users Card */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Usuarios</h3>
                <span className="text-2xl">👥</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Activos:</span>
                  <span className="font-semibold text-green-600">{stats.users.active}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Inactivos:</span>
                  <span className="font-semibold text-gray-600">{stats.users.inactive}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Límite:</span>
                  <span className="font-semibold text-blue-600">
                    {formatLimit(stats.users.limit)}
                  </span>
                </div>
                {!isUnlimited(stats.users.limit) && (
                <div className="mt-4">
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>Uso</span>
                    <span>{stats.users.percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        stats.users.percentage >= 90 ? 'bg-red-500' :
                        stats.users.percentage >= 70 ? 'bg-yellow-500' :
                        'bg-green-500'
                      }`}
                      style={{ width: `${stats.users.percentage}%` }}
                    />
                  </div>
                </div>
                )}
              </div>
            </div>

            {/* Projects Card */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Proyectos</h3>
                <span className="text-2xl">📁</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Activos:</span>
                  <span className="font-semibold text-green-600">{stats.projects.active}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Completados:</span>
                  <span className="font-semibold text-blue-600">{stats.projects.completed}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Suspendidos:</span>
                  <span className="font-semibold text-yellow-600">{stats.projects.suspended}</span>
                </div>
                <div className="flex justify-between text-sm font-semibold pt-2 border-t">
                  <span className="text-gray-900">Total:</span>
                  <span className="text-gray-900">{stats.projects.total}</span>
                </div>
              </div>
            </div>

            {/* Expenses Card */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Gastos</h3>
                <span className="text-2xl">💰</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Mes actual:</span>
                  <span className="font-semibold text-gray-900">
                    ${stats.expenses.currentMonth.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Mes anterior:</span>
                  <span className="font-semibold text-gray-600">
                    ${stats.expenses.lastMonth.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total registros:</span>
                  <span className="font-semibold text-blue-600">{stats.expenses.count}</span>
                </div>
                <div className="flex justify-between text-sm font-semibold pt-2 border-t">
                  <span className="text-gray-900">Total histórico:</span>
                  <span className="text-gray-900">
                    ${stats.expenses.total.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">
                Usuarios de {company.name}
              </h2>
              <button
                onClick={handleCreateUser}
                disabled={isAtLimit(stats.users.active, stats.users.limit)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Crear Usuario
              </button>
            </div>

            {usersLoading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : (
              <UserList 
                users={Array.isArray(users) ? users : []} 
                onEdit={handleEditUser} 
              />
            )}
          </div>
        )}

        {activeTab === 'projects' && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Proyectos de {company.name}
            </h2>
            {projectsLoading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : projects && projects.length > 0 ? (
              <div className="bg-white shadow rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proyecto</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Presupuesto</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Gastado</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Uso</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {projects.map((project) => {
                      const pct = project.budget > 0 ? Math.round((project.spent / project.budget) * 100) : 0;
                      return (
                        <tr key={project.projectId} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">{project.name}</div>
                            {project.description && (
                              <div className="text-xs text-gray-500 truncate max-w-xs">{project.description}</div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              project.status === 'active' ? 'bg-green-100 text-green-800' :
                              project.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {project.status === 'active' ? 'Activo' :
                               project.status === 'completed' ? 'Completado' : 'Suspendido'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right text-sm text-gray-900">
                            ${project.budget.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 text-right text-sm text-gray-900">
                            ${project.spent.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-16 bg-gray-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full ${
                                    pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-yellow-500' : 'bg-green-500'
                                  }`}
                                  style={{ width: `${Math.min(pct, 100)}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-600 w-10 text-right">{pct}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-12 text-center text-sm text-gray-500">
                No hay proyectos en esta empresa
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Configuración de la Empresa</h2>
            
            {/* Company Information */}
            <div className="space-y-6 mb-8">
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Información General</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nombre</label>
                  <p className="mt-1 text-sm text-gray-900">{company.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">RUT</label>
                  <p className="mt-1 text-sm text-gray-900">{company.rut}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email de contacto</label>
                  <p className="mt-1 text-sm text-gray-900">{company.contactEmail || 'No especificado'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Teléfono de contacto</label>
                  <p className="mt-1 text-sm text-gray-900">{company.contactPhone || 'No especificado'}</p>
                </div>
              </div>
            </div>

            {/* Subscription Management */}
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Gestión de Suscripción</h3>
              
              {/* Current Plan */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Plan Actual</label>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      {company.settings.subscriptionPlan.charAt(0).toUpperCase() + company.settings.subscriptionPlan.slice(1)}
                    </span>
                  </div>
                  <button
                    onClick={() => handleOpenAction('plan')}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Cambiar Plan
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Usuarios:</span>
                    <span className="ml-2 font-semibold">
                      {formatLimit(stats.users.limit)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Precio:</span>
                    <span className="ml-2 font-semibold">${company.settings.priceCLP.toLocaleString()}/mes</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Gastos:</span>
                    <span className="ml-2 font-semibold">
                      {formatLimitPlural(company.settings.maxExpensesPerMonth ?? 0)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Current Status */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estado de Suscripción</label>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      company.settings.subscriptionStatus === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : company.settings.subscriptionStatus === 'suspended'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {company.settings.subscriptionStatus === 'active' ? 'Activa' : 
                       company.settings.subscriptionStatus === 'suspended' ? 'Suspendida' : 'Cancelada'}
                    </span>
                  </div>
                  <button
                    onClick={() => handleOpenAction('status')}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                    Cambiar Estado
                  </button>
                </div>
                {company.settings.subscriptionStatus !== 'active' && (
                  <div className="mt-2 text-sm text-yellow-700 bg-yellow-50 rounded p-3">
                    ⚠️ La empresa tiene acceso limitado mientras la suscripción no esté activa.
                  </div>
                )}
              </div>
            </div>

            {/* Portal Personalizado */}
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Portal Personalizado</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${company.settings.portalEnabled ? 'bg-green-100' : 'bg-gray-200'}`}>
                      <Globe className={`w-5 h-5 ${company.settings.portalEnabled ? 'text-green-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {company.settings.portalEnabled ? 'Portal habilitado' : 'Portal no habilitado'}
                      </p>
                      {company.settings.portalEnabled && company.settings.customSubdomain && (
                        <p className="text-sm text-gray-600">
                          {company.settings.customSubdomain}.kessan.cl
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {company.settings.portalEnabled && company.settings.customSubdomain && (
                      <a
                        href={`https://${company.settings.customSubdomain}.kessan.cl`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800"
                      >
                        Visitar <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                    <button
                      onClick={() => handleOpenAction('portal')}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Configurar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* User Form Modal */}
      {showUserForm && (
        <UserForm
          user={editingUser}
          open={showUserForm}
          onClose={() => {
            setShowUserForm(false);
            setEditingUser(null);
          }}
          limitStatus={{
            activeUsers: stats.users.active,
            maxUsers: stats.users.limit,
            percentage: isUnlimited(stats.users.limit) ? 0 : stats.users.percentage,
            hasWarning: hasLimitWarning(stats.users.percentage, stats.users.limit),
            isAtLimit: isAtLimit(stats.users.active, stats.users.limit),
            availableSlots: getAvailableSlots(stats.users.active, stats.users.limit),
          }}
        />
      )}

      {/* Company Actions Modal */}
      {showActionModal && actionType && (
        <CompanyActions
          company={company}
          actionType={actionType}
          onClose={handleCloseAction}
        />
      )}
    </div>
  );
};

export default CompanyDashboard;
